import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  ButtonGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import { useSnackbar } from "notistack";

type ConformiteType = "conforme" | "non conforme";

interface ChecklistItem {
  label: string;
  key: keyof ChecklistState;
  value: ConformiteType;
}

interface ChecklistState {
  lights: ConformiteType;
  brakes: ConformiteType;
  tires: ConformiteType;
  documents: ConformiteType;
  comments: string;
}

const Checklist = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("return") === "true";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // État pour la checklist
  const [checklist, setChecklist] = useState<ChecklistState>({
    lights: "conforme",
    brakes: "conforme",
    tires: "conforme",
    documents: "conforme",
    comments: "",
  });

  // Liste des éléments à vérifier
  const checklistItems: ChecklistItem[] = [
    { label: "Feux et éclairage", key: "lights", value: checklist.lights },
    { label: "Système de freinage", key: "brakes", value: checklist.brakes },
    { label: "État des pneus", key: "tires", value: checklist.tires },
    {
      label: "Documents de bord",
      key: "documents",
      value: checklist.documents,
    },
  ];

  // Charger les détails de l'affectation
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await AssignmentService.getById(id);
        console.log("[Checklist] Affectation récupérée:", data);

        // Vérifier si l'affectation est dans la bonne étape
        const isChecklistStep =
          data.currentStep === "checklist" ||
          data.currentStep === "checklist_retour";

        const isSignaturesStep =
          data.currentStep === "signatures_entree" ||
          data.currentStep === "signatures_retour";

        if (!isChecklistStep && !isSignaturesStep) {
          console.log(
            "[Checklist] L'étape actuelle ne correspond pas à celle attendue:",
            data.currentStep
          );

          // Si nous sommes dans un retour et que l'étape est vérification_proprete_retour, tenter le passage à checklist_retour
          if (isReturn && data.currentStep === "verification_proprete_retour") {
            console.log(
              "[Checklist] Tentative de passage à l'étape checklist_retour"
            );
            try {
              const updatedAssignment = await AssignmentService.nextStep(id);
              setAssignment(updatedAssignment);

              if (updatedAssignment.currentStep !== "checklist_retour") {
                setError(
                  "Impossible d'atteindre l'étape de checklist technique (retour)"
                );
              }
            } catch (syncError) {
              console.error(
                "[Checklist] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          }
          // Si nous ne sommes pas dans un retour et que l'étape est verification_proprete, tenter le passage à checklist
          else if (!isReturn && data.currentStep === "verification_proprete") {
            console.log("[Checklist] Tentative de passage à l'étape checklist");
            try {
              const updatedAssignment = await AssignmentService.nextStep(id);
              setAssignment(updatedAssignment);

              if (updatedAssignment.currentStep !== "checklist") {
                setError(
                  "Impossible d'atteindre l'étape de checklist technique"
                );
              }
            } catch (syncError) {
              console.error(
                "[Checklist] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          } else {
            // Si nous sommes dans une autre étape non compatible
            setAssignment(data);
            setError(
              `L'étape actuelle (${data.currentStep}) ne correspond pas à la checklist technique`
            );
          }
        } else {
          // L'étape est correcte ou déjà avancée aux signatures, initialiser normalement
          setAssignment(data);

          // Initialiser la checklist si elle existe déjà
          if (isReturn && data.finalState?.checklist) {
            setChecklist({
              lights: data.finalState.checklist.lights || "conforme",
              brakes: data.finalState.checklist.brakes || "conforme",
              tires: data.finalState.checklist.tires || "conforme",
              documents: data.finalState.checklist.documents || "conforme",
              comments: data.finalState.checklist.comments || "",
            });
          } else if (data.initialState?.checklist) {
            setChecklist({
              lights: data.initialState.checklist.lights || "conforme",
              brakes: data.initialState.checklist.brakes || "conforme",
              tires: data.initialState.checklist.tires || "conforme",
              documents: data.initialState.checklist.documents || "conforme",
              comments: data.initialState.checklist.comments || "",
            });
          }
        }
      } catch (err) {
        console.error(
          "[Checklist] Erreur lors du chargement de l'affectation:",
          err
        );
        setError("Impossible de charger les détails de l'affectation");
        enqueueSnackbar("Erreur lors du chargement de l'affectation", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, enqueueSnackbar, isReturn]);

  // Mettre à jour l'état de la checklist
  const handleChecklistChange = (
    field: keyof ChecklistState,
    value: string | ConformiteType
  ) => {
    setChecklist({
      ...checklist,
      [field]: value,
    });
  };

  // Soumettre la checklist
  const handleSubmit = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log("[Checklist] Début du processus de soumission");

      // Récupérer d'abord l'état actuel pour s'assurer que nous avons les dernières données
      console.log("[Checklist] Récupération de l'état actuel");
      const currentAssignment = await AssignmentService.getById(id);
      console.log("[Checklist] État actuel:", currentAssignment);

      // Vérifier si l'étape actuelle est cohérente avec l'action
      const isChecklistStep =
        currentAssignment.currentStep === "checklist" ||
        currentAssignment.currentStep === "checklist_retour";

      const isSignaturesStep =
        currentAssignment.currentStep === "signatures_entree" ||
        currentAssignment.currentStep === "signatures_retour";

      if (!isChecklistStep && !isSignaturesStep) {
        const errorMessage =
          "L'étape actuelle ne correspond pas à la checklist";
        console.error("[Checklist] Erreur d'étape:", errorMessage);
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
        setSubmitting(false);
        return;
      }

      // Vérifier si l'étape est déjà avancée
      const isAlreadyInNextStep = isSignaturesStep;

      // Selon que c'est un retour ou une affectation initiale
      const isChecklistRetour =
        currentAssignment.currentStep === "checklist_retour" ||
        currentAssignment.currentStep === "signatures_retour";
      console.log("[Checklist] Est un retour?", isChecklistRetour);

      if (!isAlreadyInNextStep) {
        // Envoyer les données de checklist
        console.log("[Checklist] Données de checklist à envoyer:", checklist);
        if (isChecklistRetour) {
          console.log("[Checklist] Envoi de l'état de checklist final");
          await AssignmentService.updateChecklist(id, {
            ...checklist,
            isReturn: true,
          });
          enqueueSnackbar("Checklist finale enregistrée avec succès", {
            variant: "success",
          });
        } else {
          console.log("[Checklist] Envoi de l'état de checklist initial");
          await AssignmentService.updateChecklist(id, checklist);
          enqueueSnackbar("Checklist initiale enregistrée avec succès", {
            variant: "success",
          });
        }
      } else {
        console.log(
          "[Checklist] Étape déjà avancée à signatures, pas besoin d'envoyer de données"
        );
      }

      // Passer à l'étape suivante SEULEMENT après l'envoi des données (ou si déjà à l'étape suivante)
      let updatedAssignment = currentAssignment;

      if (!isAlreadyInNextStep) {
        console.log("[Checklist] Passage à l'étape suivante");
        updatedAssignment = await AssignmentService.nextStep(id);
        console.log(
          "[Checklist] Nouvel état après nextStep:",
          updatedAssignment
        );
      } else {
        console.log(
          "[Checklist] Déjà à l'étape suivante, pas besoin d'appeler nextStep"
        );
      }

      // Mettre à jour l'état local avec la dernière version
      setAssignment(updatedAssignment);

      // Redirection vers la page de signatures
      console.log("[Checklist] Redirection vers la page de signatures");
      if (isChecklistRetour) {
        navigate(`/signatures/${id}?return=true`);
      } else {
        navigate(`/signatures/${id}`);
      }
    } catch (err) {
      console.error(
        "[Checklist] Erreur lors de l'enregistrement de la checklist:",
        err
      );
      enqueueSnackbar("Erreur lors de l'enregistrement de la checklist", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Gérer le retour à l'étape précédente
  const handlePreviousStep = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log(
        "[Checklist] Début du processus de retour à l'étape précédente"
      );

      // Récupérer d'abord l'état actuel pour s'assurer que nous avons les dernières données
      console.log("[Checklist] Récupération de l'état actuel");
      const currentAssignment = await AssignmentService.getById(id);
      console.log(
        "[Checklist] État actuel avant previousStep:",
        currentAssignment
      );

      // Passer à l'étape précédente
      console.log("[Checklist] Appel de previousStep");
      const updatedAssignment = await AssignmentService.previousStep(id);
      console.log("[Checklist] État après previousStep:", updatedAssignment);

      // Mettre à jour l'affectation locale
      setAssignment(updatedAssignment);

      // Redirection vers la page précédente
      if (currentAssignment.currentStep === "checklist_retour") {
        console.log(
          "[Checklist] Redirection vers inspection-proprete (retour)"
        );
        // Si nous sommes dans la phase de retour, revenir à la vérification de propreté retour
        navigate(`/inspection-proprete/${id}?return=true`);
      } else {
        console.log("[Checklist] Redirection vers inspection-proprete");
        // Si nous sommes dans la phase initiale, revenir à la vérification de propreté
        navigate(`/inspection-proprete/${id}`);
      }
    } catch (err) {
      console.error(
        "[Checklist] Erreur lors du retour à l'étape précédente:",
        err
      );
      enqueueSnackbar("Erreur lors du retour à l'étape précédente", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !assignment) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          backgroundColor: "#f9f9f9",
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              {error || "Affectation non trouvée"}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mt: 2 }}
            >
              Retour à la liste
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
        <Box sx={{ p: 3, minHeight: "100vh" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 2 : 0,
              mb: 4,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mr: 2 }}
              size={isMobile ? "medium" : "large"}
            >
              Retour
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Checklist technique {isReturn ? "(retour)" : "(entrée)"}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1">
              Veuillez vérifier les éléments techniques du véhicule et remplir
              la checklist.
              <strong>
                {" "}
                À l'étape suivante, les signatures du gestionnaire et du
                conducteur seront requises.
              </strong>
            </Typography>
          </Alert>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Information sur l'affectation
              </Typography>
              <Grid container spacing={2}>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Véhicule:</strong> {assignment.vehiclePlate}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Chauffeur:</strong> {assignment.driverFirstName}{" "}
                    {assignment.driverLastName}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Date:</strong>{" "}
                    {new Date(assignment.date).toLocaleDateString("fr-FR")}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Kilométrage:</strong> {assignment.startMileage} km
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Checklist Technique
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {checklistItems.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.key}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border:
                          item.value === "conforme"
                            ? "1px solid #4caf50"
                            : item.value === "non conforme"
                            ? "1px solid #f44336"
                            : "1px solid #e0e0e0",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <CardHeader
                        title={item.label}
                        titleTypographyProps={{
                          variant: "h6",
                          fontWeight: 500,
                        }}
                        sx={{
                          backgroundColor:
                            item.value === "conforme"
                              ? "rgba(76, 175, 80, 0.1)"
                              : item.value === "non conforme"
                              ? "rgba(244, 67, 54, 0.1)"
                              : "rgba(0, 0, 0, 0.03)",
                          borderBottom: "1px solid #eee",
                          pb: 1,
                        }}
                      />
                      <CardContent sx={{ pt: 2 }}>
                        <ButtonGroup fullWidth variant="outlined" size="large">
                          <Button
                            color="success"
                            variant={
                              item.value === "conforme"
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() =>
                              handleChecklistChange(item.key, "conforme")
                            }
                            startIcon={<CheckIcon />}
                            sx={{ py: 1 }}
                          >
                            Conforme
                          </Button>
                          <Button
                            color="error"
                            variant={
                              item.value === "non conforme"
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() =>
                              handleChecklistChange(item.key, "non conforme")
                            }
                            startIcon={<ClearIcon />}
                            sx={{ py: 1 }}
                          >
                            Non conforme
                          </Button>
                        </ButtonGroup>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Commentaires
              </Typography>
              <Grid sx={{ gridColumn: { xs: "1 / 13" } }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={checklist.comments || ""}
                  onChange={(e) =>
                    handleChecklistChange("comments", e.target.value)
                  }
                  placeholder="Ajouter des commentaires techniques ou observations supplémentaires..."
                />
              </Grid>
            </Box>
          </Paper>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/assignments")}
              fullWidth={isMobile}
            >
              Annuler
            </Button>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePreviousStep}
                disabled={submitting}
                fullWidth={isMobile}
              >
                Étape précédente
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
                fullWidth={isMobile}
              >
                {submitting ? "Enregistrement..." : "Continuer"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Checklist;
